import { useQueryClient } from '@tanstack/react-query'
import { Col, Flex, Form, Input, Row } from 'antd'
import { FormInstance } from 'antd/lib'
import { useEffect, useState } from 'react'

import {
  useInvestigationsServiceGetInvestigationDemandListEndpoint,
  useInvestigationsServiceGetInvestigationDemandListEndpointKey
} from 'src/api/queries'
import { ClassifierCategory, ClassifierType } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { SearchBar, message, ClassifierInfiniteSelect } from 'src/components'
import { getFullName } from 'src/features/users/utils'
import { useClassifierId } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  form: FormInstance
}

export const AutomaticallyAddInvestigation = ({ form }: Props) => {
  const { t } = useLocales()

  const [search, setSearch] = useState<string>('')
  const queryClient = useQueryClient()

  const classifierId = useClassifierId(
    ClassifierType.TEST_TYPE,
    ClassifierCategory.INVESTIGATIONS
  )
  const specimenTypeId = useClassifierId(
    ClassifierType.SPECIMEN_TYPE,
    ClassifierCategory.INVESTIGATIONS
  )

  const { data, refetch } =
    useInvestigationsServiceGetInvestigationDemandListEndpoint(
      { code: search },
      [],
      {
        enabled: false
      }
    )

  const onSearch = (val: string) => {
    queryClient.removeQueries({
      queryKey: [useInvestigationsServiceGetInvestigationDemandListEndpointKey]
    })
    setSearch(val)
  }

  useEffect(() => {
    if (search) {
      refetch()
    }
  }, [search])

  useEffect(() => {
    if (search && data?.data?.length === 0) {
      message.error(t('investigations.not_found_investigation'))
      form.resetFields()
    }
  }, [data, search])

  useEffect(() => {
    if (search && data?.data?.length !== 0) {
      const investigation = data?.data?.[0]

      form.setFieldsValue({
        institutionId: investigation?.institutionUser?.institution?.id,
        institutionName: investigation?.institutionUser?.institution?.name,
        doctorId: investigation?.institutionUser?.id,
        doctorName: getFullName(
          investigation?.institutionUser?.firstName,
          investigation?.institutionUser?.lastName
        ),
        simeCode: investigation?.person?.simeCode,
        tarvCode: investigation?.person?.tarvCode,
        personId: investigation?.person?.id,
        testTypeId: investigation?.testType?.id,
        specimenType: investigation?.specimenTypes?.[0]?.id
      })
    }
  }, [data, search])

  return (
    <Flex vertical gap={24}>
      <Flex justify="space-between" gap={8}>
        <SearchBar
          onSearch={onSearch}
          maxWidth={1800}
          placeholder={t('investigations.search_by_code')}
        />
      </Flex>

      <Row gutter={[24, 24]} key={search}>
        <Col span={12}>
          <Form.Item
            name="institutionName"
            label={t('common.medical_institution')}
            rules={requiredRules}
          >
            <Input disabled placeholder={t('common.medical_institution')} />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name="doctorName"
            label={t('analysis.medic_modal_title')}
            rules={requiredRules}
          >
            <Input disabled placeholder={t('analysis.medic_modal_title')} />
          </Form.Item>
        </Col>

        {data?.data?.[0].person?.simeCode && (
          <Col span={12}>
            <Form.Item name="simeCode" label={t('common.sime_code')}>
              <Input disabled placeholder={t('common.sime_code')} />
            </Form.Item>
          </Col>
        )}

        {data?.data?.[0].person?.tarvCode && (
          <Col span={12}>
            <Form.Item name="tarvCode" label={t('common.tarv_code')}>
              <Input disabled placeholder={t('common.tarv_code')} />
            </Form.Item>
          </Col>
        )}

        <Col span={12}>
          <Form.Item
            name="testTypeId"
            label={t('analysis.type')}
            rules={requiredRules}
          >
            <ClassifierInfiniteSelect
              id={classifierId}
              placeholder={t('analysis.type')}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name="specimenType"
            label={t('consultations.specimen_type')}
            rules={requiredRules}
          >
            <ClassifierInfiniteSelect
              id={specimenTypeId}
              placeholder={t('consultations.specimen_type')}
            />
          </Form.Item>
        </Col>
      </Row>
    </Flex>
  )
}
